import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import Layout from "../common/Layout";
import HeroSlider from "../common/HeroSlider";
import NewsletterSignup from "../common/NewsletterSignup";
import EhoBadge from "../common/EhoBadge";
import { track } from "../features/analytics/tracker";
import { addToCart } from "../features/cart/cart";
import { useBoxes } from "../assets/utils/useBoxes";
import HarvestStrip from "../common/HarvestStrip";
import { usePageMeta, useJsonLd, LOCAL_BUSINESS_LD } from "../common/usePageMeta";

const categories = [
  { name: "Fresh Greens",    link: "/browse-products?category=salads",   img: "/images/products/lettuce/IMG_4447.JPEG" },
  { name: "Culinary Herbs",  link: "/browse-products?category=herbs",    img: "/images/products/genoBasil/IMG_4565.JPEG" },
  { name: "Microgreens",     link: "/browse-products?category=micro",    img: "/images/products/cress/IMG_4581.JPEG" }
];

const testimonials = [
  { text: "Fantastic product. Really fresh and lasted ages",                                  author: "Home Cook, Rayleigh" },
  { text: "A very good basil - seemed super premium",                                         author: "Home Cook, Rayleigh" },
  { text: "Herbs so fresh and lasted much longer than shop bought ones",                      author: "Verified customer" },
  { text: "Elevates our salads and sandwiches, on its own or with other salad items, keeps well too", author: "Verified customer" },
  { text: "Lovely variety and great flavour, will definitely be buying again",               author: "Verified customer" },
  { text: "Tasty stuff, and we love that it's local",                                        author: "Verified customer" },
];

/* Hard stats from the leaflets */
const STATS = [
  { value: "2×",    label: "Longer shelf life",       icon: "📅", detail: "vs supermarket equivalents" },
  { value: "0",     label: "Chemical pesticides",     icon: "🌿", detail: "ladybirds, not chemicals" },
  { value: "~3mi",  label: "Farm to fork",            icon: "🚚", detail: "local Essex delivery only" },
  { value: "<2hr",  label: "Harvest to your door",    icon: "⏱️", detail: "always same-day, always fresh" },
];

function parseCategoryParam(link) {
  try {
    const u = new URL(link, window.location.origin);
    return u.searchParams.get("category");
  } catch { return null; }
}

/* Next delivery badge - same logic as browse page */
function NextDeliveryBadge() {
  const getNext = () => {
    const now = new Date();
    const totalMins = now.getHours() * 60 + now.getMinutes();
    if (totalMins < 600) return { label: "Morning delivery", when: "10:30am today", urgent: 600 - totalMins < 90 };
    if (totalMins < 1020) return { label: "Evening delivery", when: "5:30pm today", urgent: 1020 - totalMins < 90 };
    return { label: "Next delivery", when: "tomorrow 10:30am", urgent: false };
  };
  const [info, setInfo] = useState(getNext());
  useEffect(() => {
    const id = setInterval(() => setInfo(getNext()), 60000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className={`next-delivery-badge ${info.urgent ? "is-urgent" : ""}`}>
      {info.urgent ? "⚡" : "🚚"} {info.label} - {info.when}
    </span>
  );
}

/* ── Trust band: hard stats + EHO rating in one strip ── */
function StatsStrip() {
  return (
    <section className="section" style={{ paddingTop: "var(--s-5)", paddingBottom: 0 }}>
      <div className="container">
        <div className="hard-stats-strip">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              className="hard-stat"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
            >
              <span className="hard-stat-icon">{s.icon}</span>
              <strong className="hard-stat-value">{s.value}</strong>
              <span className="hard-stat-label">{s.label}</span>
              <span className="hard-stat-detail">{s.detail}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Comparison table removed from the homepage — the full version lives on the
   How It Works page; the teaser section below links to it. */

// Replace the hardcoded TASTER_BAG constant and TasterBagSection with this:

function TasterBagSection() {
  const { boxes, loading } = useBoxes();
  const [addState, setAddState] = useState("idle");

  // Always show only the first enabled bag on the homepage
  const bag = boxes[0] ?? null;

  if (loading) return (
    <section className="section alt">
      <div className="container">
        <div className="taster-bag-home" style={{ minHeight: 260 }}>
          <div className="skeleton" style={{ height: "100%", borderRadius: 12 }} />
        </div>
      </div>
    </section>
  );

  if (!bag) return null;

  
  const saving = bag.originalPrice
    ? (bag.originalPrice - bag.price).toFixed(2)
    : null;

  const handleAdd = () => {
    if (addState !== "idle") return;
    setAddState("adding");
    track("home_taster_bag_add_to_cart");
    addToCart({
      id:     `box-${bag.id}`,
      baseId: `box-${bag.id}`,
      name:   `${bag.name}${bag.grams ? ` (${bag.grams})` : ""}`,
      price:  bag.price,
      qty:    1,
      grams:  0,
      image:  bag.image,
    });
    setTimeout(() => setAddState("done"), 300);
    setTimeout(() => setAddState("idle"), 2200);
  };

  return (
    <section className="section alt">
      <div className="container">
        <div style={{ textAlign: "center", marginBottom: "var(--s-5)" }}>
          <p className="eyebrow">New here?</p>
          <h2 className="h2">Start with the Taster Bag</h2>
          <p className="lead u-maxw-28r" style={{ margin: "0 auto" }}>
            Not sure what to try first? We'll pick the best of today's harvest for you.
          </p>
        </div>

        <motion.div
          className="taster-bag-home"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.45 }}
        >
          <div
            className="taster-bag-home-media"
            style={{ backgroundImage: `url(${bag.image})` }}
          >
            {saving && <div className="taster-bag-save-pill home-save-pill">Save £{saving}</div>}
          </div>
          <div className="taster-bag-home-body">
            <div className="taster-bag-home-badges">
              {bag.badges?.map(b => (
                <span key={b} className="taster-bag-badge">{b}</span>
              ))}
            </div>
            <h3 className="h3" style={{ margin: "var(--s-3) 0 var(--s-2)" }}>{bag.name}</h3>
            <p className="muted" style={{ marginBottom: "var(--s-4)" }}>{bag.description}</p>

            {bag.contents?.length > 0 && (
              <div className="taster-bag-contents" style={{ marginBottom: "var(--s-4)" }}>
                <span className="taster-bag-contents-label">Typically includes:</span>
                {bag.contents.map(c => (
                  <span key={c} className="taster-bag-content-pill">{c}</span>
                ))}
              </div>
            )}

            <div className="taster-bag-home-buy">
              <div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span className="taster-bag-price">£{bag.price.toFixed(2)}</span>
                  {bag.originalPrice && (
                    <span className="taster-bag-was">was £{bag.originalPrice.toFixed(2)}</span>
                  )}
                </div>
                <NextDeliveryBadge />
              </div>
              <button
                className={`btn ${addState === "done" ? "btn-secondary" : "btn-primary"}`}
                style={{ minWidth: 180, marginTop: "var(--s-3)" }}
                onClick={handleAdd}
                disabled={addState === "adding"}
              >
                {addState === "adding" ? "Adding…" : addState === "done" ? "✓ In your cart!" : `Add to Cart · £${bag.price.toFixed(2)}`}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function StarRow({ rating, small }) {
  if (typeof rating !== "number") return null;
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
  return (
    <span className={`google-stars${small ? " google-stars--sm" : ""}`} aria-label={`${rating} out of 5 stars`}>
      <span className="google-stars-track">★★★★★</span>
      <span className="google-stars-fill" style={{ width: `${pct}%` }}>★★★★★</span>
    </span>
  );
}

function GoogleGIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.98v2.33A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.98A9 9 0 0 0 0 9c0 1.45.35 2.83.98 4.03l2.97-2.33z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .98 4.97l2.97 2.33C4.66 5.17 6.65 3.58 9 3.58z"/>
    </svg>
  );
}

// Native scroll-snap track, so touch swipe comes free; the arrows just move it
// one card at a time. Positions are measured rather than assumed because how
// many cards fit depends on the breakpoint, and the controls disappear when
// they all fit.
function GoogleReviewsCarousel({ reviews }) {
  const trackRef = useRef(null);
  const [position, setPosition] = useState(0);
  const [positions, setPositions] = useState(1);

  const stepOf = (el) => {
    const [first, second] = el.children;
    return second ? second.offsetLeft - first.offsetLeft : el.clientWidth;
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => {
      const step = stepOf(el);
      setPositions(Math.round((el.scrollWidth - el.clientWidth) / step) + 1);
      setPosition(Math.round(el.scrollLeft / step));
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      obs.disconnect();
    };
  }, [reviews]);

  const move = (delta) => {
    const el = trackRef.current;
    el.scrollTo({ left: (position + delta) * stepOf(el) });
    track("home_google_reviews_arrow_click", { direction: delta > 0 ? "next" : "prev" });
  };

  return (
    <div className="google-reviews-carousel">
      <div className="google-reviews-track" ref={trackRef}>
        {reviews.map((r, i) => (
          <figure className="testimonial-card testimonial-card--google" key={i}>
            <blockquote className="quote-text">{r.text}</blockquote>
            <figcaption className="google-review-by">
              {r.profilePhotoUrl && (
                <img className="google-review-avatar" src={r.profilePhotoUrl} alt="" />
              )}
              <span className="google-review-meta">
                <span className="author">{r.author}</span>
                <StarRow rating={r.rating} small />
                <span className="google-review-time">{r.relativeTimeDescription}</span>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>
      {positions > 1 && (
        <div className="google-reviews-controls">
          <button type="button" className="icon-btn" aria-label="Previous reviews" disabled={position <= 0} onClick={() => move(-1)}>
            <ChevronLeft size={20} aria-hidden="true" />
          </button>
          <div className="testi-dots" aria-hidden="true">
            {Array.from({ length: positions }, (_, i) => (
              <span key={i} className={`dot ${i === position ? "is-active" : ""}`} />
            ))}
          </div>
          <button type="button" className="icon-btn" aria-label="Next reviews" disabled={position >= positions - 1} onClick={() => move(1)}>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ================================================================
   MAIN PAGE
================================================================ */
export default function HomePage() {
  const [tIdx, setTIdx] = useState(0);
  const [googleReviews, setGoogleReviews] = useState(null);
  const seenSections = useRef(new Set());

  useEffect(() => {
    const id = setInterval(() => setTIdx(i => (i + 1) % testimonials.length), 7000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetch("https://teardrophydro.co.uk/garageFarm/reviews")
      .then(r => r.json())
      .then(setGoogleReviews)
      .catch(() => {});
  }, []);

  const fadeUp = useMemo(() => ({
    initial:    { opacity: 0, y: 12 },
    animate:    { opacity: 1, y: 0  },
    transition: { duration: 0.6, ease: "easeOut" },
  }), []);

  useEffect(() => {
    const fired = new Set();
    const milestones = [10, 25, 50, 75, 90, 100];
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const total    = document.documentElement.scrollHeight;
      const pct      = Math.round((scrolled / total) * 100);
      for (const m of milestones) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          track("home_scroll_depth", { pct: m });
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Tracks how far down the page real visitors actually get, section by
  // section, so drop-off is visible in analytics instead of inferred from
  // scroll % alone. Re-scans on every render since sections like Google
  // Reviews mount conditionally, well after the initial pass; seenSections
  // persists across re-scans so nothing double-fires.
  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const key = e.target.getAttribute("data-track-key");
        if (!key || seenSections.current.has(key)) continue;
        seenSections.current.add(key);
        track("home_section_view", { section: key });
        if (key === "newsletter") track("newsletter_view", { page: "home" });
      }
    }, { threshold: 0.35 });

    document.querySelectorAll("[data-track-key]").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [googleReviews]);

  const t = testimonials[tIdx];

  usePageMeta({
    title: "TearDrop Hydro - Fresh herbs & salads, grown in Hockley, Essex",
    description:
      "Fresh herbs, salad leaves and microgreens grown hydroponically in Hockley and delivered across Essex within hours of harvest. No pesticides, no food miles.",
    path: "/",
  });
  useJsonLd("local-business", LOCAL_BUSINESS_LD);

  return (
    <Layout>
      {/* HERO — same treatment as the How/Partner pages, photos still rotate */}
      <section data-track-key="hero" style={{ position: "relative", overflow: "hidden", minHeight: "clamp(380px, 55svh, 620px)", display: "flex", alignItems: "flex-end" }}>
        <HeroSlider fill />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,28,20,.82) 0%, rgba(10,28,20,.52) 60%, transparent 100%)" }} />
        <div className="container" style={{ position: "relative", zIndex: 1, padding: "var(--s-10) var(--s-5) var(--s-9)" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="eyebrow" style={{ color: "var(--accent)", marginBottom: "var(--s-3)" }}>
              Grown in Hockley · Delivered in Essex
            </p>
            <h1 className="h1" style={{ color: "#fff", maxWidth: 580, marginTop: 0, marginBottom: "var(--s-6)", fontSize: "clamp(28px,5vw,44px)", lineHeight: 1.1 }}>
              The freshest greens in Essex.{" "}
              <em>Harvested this morning, on your plate tonight.</em>
            </h1>
            <div className="hero-ctas">
              <Link to="/browse-products" className="btn btn-primary" onClick={() => track("home_hero_cta_click", { cta: "shop_now" })}>
                Shop Today's Harvest
              </Link>
            </div>
            <a
              href="https://www.instagram.com/teardrop_hydro/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: "var(--s-4)", color: "rgba(255,255,255,.8)", fontSize: 14, fontWeight: 600 }}
              onClick={() => track("home_hero_instagram_click")}
            >
              <Instagram size={16} />
              Follow the harvest @teardrop_hydro
            </a>
          </motion.div>
        </div>
      </section>

      {/* THIS WEEK'S HARVEST - controlled from the farm dashboard */}
      <div data-track-key="harvest_strip"><HarvestStrip /></div>

      {/* TRUST BAND - hard stats + EHO rating */}
      {/* <StatsStrip /> */}

      {/* TASTER BAG - near the top, before categories */}
      <div data-track-key="taster_bag"><TasterBagSection /></div>

      {/* CATEGORIES */}
      <section className="section" data-track-key="categories">
        <div className="container">
          <motion.h3 {...fadeUp} className="h2">What We Grow</motion.h3>
          <div className="cat-scroller" role="list">
            {categories.map(c => {
              const catParam = parseCategoryParam(c.link);
              return (
                <Link
                  key={c.name}
                  to={c.link}
                  role="listitem"
                  className="category-card"
                  style={{ backgroundImage: `url(${c.img})` }}
                  onClick={() => track("home_category_click", { name: c.name, category: catParam, link: c.link })}
                >
                  <div className="category-overlay">
                    <h4 className="category-title">{c.name}</h4>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS TEASER - the full comparison lives on the How page */}
      <section className="section" data-track-key="hiw_teaser" style={{ paddingTop: "var(--s-4)", paddingBottom: "var(--s-4)" }}>
        <div className="container" style={{ textAlign: "center" }}>
          <p className="lead u-maxw-28r" style={{ margin: "0 auto var(--s-4)" }}>
            Supermarket salad is weeks old before it reaches the shelf.
            Ours is cut the morning it's delivered.
          </p>
          <Link to="/how-it-works" className="btn btn-ghost" onClick={() => track("home_hiw_link_click")}>
            See how it works
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section alt" data-track-key="testimonials">
        <div className="container">
          <h3 className="h2">Why People Choose TearDrop</h3>
          <div className="testimonial-wrap" aria-live="polite">
            <AnimatePresence mode="wait">
              <motion.figure
                key={tIdx}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.4 }}
                className="testimonial-card testimonial-card--accent"
              >
                <blockquote className="quote-text">{t.text}</blockquote>
                <figcaption className="quote-by">
                  <span className="author"> - {t.author}</span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
            <div className="testi-dots" role="tablist" aria-label="Testimonials">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`dot ${i === tIdx ? "is-active" : ""}`}
                  onClick={() => { setTIdx(i); track("home_testimonial_dot_click", { index: i }); }}
                  role="tab"
                  aria-selected={i === tIdx}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      {googleReviews?.reviews?.length > 0 && (
        <section className="section" data-track-key="google_reviews">
          <div className="container">
            <h3 className="h2">What People Say on Google</h3>
            <div className="google-reviews-header">
              <StarRow rating={googleReviews.rating} />
              <span className="google-reviews-count">
                {googleReviews.rating?.toFixed(1)} · {googleReviews.userRatingsTotal} Google reviews
              </span>
            </div>
            <GoogleReviewsCarousel reviews={googleReviews.reviews} />
            <div className="google-attribution">
              <GoogleGIcon />
              <span>Reviews from Google</span>
              {googleReviews.mapsUrl && (
                <a
                  href={googleReviews.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("home_google_reviews_link_click")}
                >
                  See all reviews on Google
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* SUBSCRIBE PROMO */}
      <section className="section" data-track-key="subscribe_promo" style={{ background: "var(--brand)", overflow: "hidden", position: "relative" }}>
        <div
          style={{
            position: "absolute", inset: 0, opacity: 0.12,
            backgroundImage: "url(/images/products/seedlings/IMG_4847.JPEG)",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="container" style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.45 }}
          >
            <p className="eyebrow" style={{ color: "var(--accent)" }}>Never run out</p>
            <h2 className="h2" style={{ color: "#fff", marginBottom: "var(--s-4)" }}>
              Set it up once. Fresh every week.
            </h2>
            <p style={{ color: "rgba(255,255,255,.8)", fontSize: "clamp(15px, 1.8vw, 17px)", maxWidth: 520, margin: "0 auto var(--s-7)" }}>
              Skip the weekly shop. Get exactly what you want- specific crops, your quantities, your schedule, delivered on repeat.
            </p>
            <a
              href="https://subscribe.teardrophydro.co.uk"
              className="btn"
              style={{
                background: "var(--accent)", color: "var(--brand)",
                fontWeight: 700, fontSize: 17,
                padding: "var(--s-4) var(--s-8)",
                borderRadius: "var(--r-md)",
                display: "inline-block",
              }}
              onClick={() => track("home_subscribe_banner_cta_click")}
            >
              Set Up a Subscription
            </a>
            <p style={{ color: "rgba(255,255,255,.55)", fontSize: 13, marginTop: "var(--s-3)" }}>
              Free to sign up · No card needed to start
            </p>
          </motion.div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="section alt" data-track-key="newsletter">
        <div className="container">
          <NewsletterSignup />
        </div>
      </section>
    </Layout>
  );
}